/**
 * Живое присутствие контрагентов.
 *
 * Кадры `presence` эфемерны и в реплику не попадают (BS §9.4): реплика знает
 * presence только на момент последней загрузки списка. Стор держит свежие
 * значения по `user_id`, чтобы «в сети» и «был(а) недавно» обновлялись в
 * «Контактах», списке чатов и шапке диалога без перезапроса — как в Telegram.
 */
import { create } from 'zustand'

import type { Presence } from '@shared/api/types'
import type { PresenceFrame } from '@shared/ws/socket'

interface PresenceState {
  /** user_id → последнее известное присутствие. */
  byUser: Record<string, Presence>
  apply: (frame: PresenceFrame) => void
}

export const usePresenceStore = create<PresenceState>((set) => ({
  byUser: {},
  apply: (frame) =>
    set((state) => {
      const presence: Presence = { online: frame.online }
      if (frame.last_seen_bucket) presence.last_seen_bucket = frame.last_seen_bucket
      return { byUser: { ...state.byUser, [frame.user_id]: presence } }
    }),
}))

/**
 * Присутствие пользователя: живой кадр, если он был, иначе — значение из реплики.
 * `null` из реплики означает «скрыто взаимной настройкой» (BS §9.5) и живым кадром
 * не перекрывается: сервер такие кадры и не присылает.
 */
export function useLivePresence(
  userId: string | null | undefined,
  fallback: Presence | null | undefined,
): Presence | null {
  const live = usePresenceStore((s) => (userId ? (s.byUser[userId] ?? null) : null))
  if (fallback === null || fallback === undefined) return live ?? null
  return live ?? fallback
}
